import { z } from "zod";
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

export const SSEMetadataEventSchema = z.object({
  type: z.literal("metadata"),
  data: z.object({
    conversationId: z.string().uuid(),
    model: z.string(),
    classification: z.object({
      sensitivity: SensitivitySchema.default("unclassified"),
      exportControl: ExportControlSchema.default("none"),
    }).optional(),
  }),
});

export const SSETokenEventSchema = z.object({
  type: z.literal("token"),
  data: z.object({ content: z.string() }),
});

export const SSEToolCallEventSchema = z.object({
  type: z.literal("tool_call"),
  data: z.object({
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()),
  }),
});

export const SSEToolResultEventSchema = z.object({
  type: z.literal("tool_result"),
  data: z.object({
    toolName: z.string(),
    result: z.unknown(),
  }),
});

export const SSECitationEventSchema = z.object({
  type: z.literal("citation"),
  data: z.object({
    // Nullable+optional: AI/Web citations have no backing document UUID.
    documentId: z.string().nullable().optional(),
    docName: z.string(),
    page: z.number().int().nullable(),
    section: z.string().nullable(),
    text: z.string(),
    confidence: z.number().min(0).max(1).optional(),
    // Source-attribution tier (defaults to 'library' for legacy/library citations).
    sourceType: z.enum(["library", "ai", "web"]).default("library"),
    subSource: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
  }),
});

export const SSEClarificationEventSchema = z.object({
  type: z.literal("clarification"),
  data: z.object({
    question: z.string(),
    options: z.array(z.object({
      label: z.string(),
      value: z.string(),
      description: z.string().optional(),
    })),
    allowFreeform: z.boolean().default(true),
  }),
});

export const SSEFollowUpEventSchema = z.object({
  type: z.literal("follow_up"),
  data: z.object({ suggestions: z.array(z.string()) }),
});

export const SSEErrorEventSchema = z.object({
  type: z.literal("error"),
  data: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

export const SSEHeartbeatEventSchema = z.object({
  type: z.literal("heartbeat"),
  data: z.object({}),
});

export const SSEDoneEventSchema = z.object({
  type: z.literal("done"),
  data: z.object({
    totalTokens: z.number().int(),
    latencyMs: z.number().int(),
  }),
});

export const SSEEventSchema = z.discriminatedUnion("type", [
  SSEMetadataEventSchema,
  SSETokenEventSchema,
  SSEToolCallEventSchema,
  SSEToolResultEventSchema,
  SSECitationEventSchema,
  SSEClarificationEventSchema,
  SSEFollowUpEventSchema,
  SSEErrorEventSchema,
  SSEHeartbeatEventSchema,
  SSEDoneEventSchema,
]);

export type SSEEvent = z.infer<typeof SSEEventSchema>;
export type SSEMetadataEvent = z.infer<typeof SSEMetadataEventSchema>;
export type SSETokenEvent = z.infer<typeof SSETokenEventSchema>;
export type SSECitationEvent = z.infer<typeof SSECitationEventSchema>;
export type SSEDoneEvent = z.infer<typeof SSEDoneEventSchema>;
